import React, { useState } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonButton, 
  IonInput, 
  IonItem, 
  IonLabel,
  IonToast,
  IonText,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonRadioGroup,
  IonRadio,
  IonList
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { personOutline, businessOutline, arrowBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { supabase, createUserProfile } from '../../services/supabase'
import { UserRole, User } from '../../types'
import LanguageSwitcher from '../../components/LanguageSwitcher'
import { useAuthStore } from '../../store/authStore'

/**
 * Registration Page Component
 * 
 * Features implemented as per requirements:
 * - Removed admin role option from registration (admins must be promoted by existing admins)
 * - Routes users to onboarding after successful registration
 * - Shows success toast with proper messaging
 * - After onboarding completion, routes to appropriate dashboard based on role
 * - Includes note about future email confirmation requirement
 */
const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<UserRole>('coachee')
  const [specializations, setSpecializations] = useState('')
  const [invitationCode, setInvitationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('danger')
  const history = useHistory()
  const { t } = useTranslation()
  const { setUser, setSupabaseUser } = useAuthStore()

  // Check URL params for invitation code
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    if (code) {
      setInvitationCode(code)
      setRole('coachee') // Default to coachee for invitation codes
    }
  }, [])

  const showToastMessage = (message: string, color: 'success' | 'danger' = 'danger') => {
    setToastMessage(message)
    setToastColor(color)
    setShowToast(true)
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Basic validation
    if (!fullName.trim()) {
      showToastMessage('Please enter your full name')
      return
    }
    
    if (!email.trim()) {
      showToastMessage('Please enter your email address')
      return
    }
    
    if (password.length < 6) {
      showToastMessage('Password must be at least 6 characters long')
      return
    }
    
    setLoading(true)

    try {
      // Validate invitation code if provided
      let coachId = null
      let invitationCodeData = null
      if (invitationCode && role === 'coachee') {
        const { data: codeData, error: codeError } = await supabase
          .from('invitation_codes')
          .select('*')
          .eq('code', invitationCode)
          .in('status', ['pending', 'accepted'])
          .gt('expires_at', new Date().toISOString())
          .single()

        if (codeError || !codeData) {
          throw new Error('Invalid or expired invitation code')
        }

        // Check if code has remaining uses
        if (codeData.current_uses >= codeData.max_uses) {
          throw new Error('This invitation code has reached its maximum number of uses')
        }

        coachId = codeData.coach_id
        invitationCodeData = codeData
      }

      // Register user with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role
          }
        }
      })

      if (authError) {
        // Handle specific error cases
        if (authError.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please try signing in instead.')
        } else if (authError.message.includes('Invalid email')) {
          throw new Error('Please enter a valid email address.')
        } else if (authError.message.includes('Password')) {
          throw new Error('Password must be at least 6 characters long.')
        } else {
          throw authError
        }
      }

      if (authData.user) {
        // Create user profile
        const profileData = {
          full_name: fullName,
          ...(role === 'coach' && specializations && {
            specializations: specializations.split(',').map(s => s.trim())
          })
        }

        console.log('Checking for existing profile or creating new one with role:', role)
        console.log('Profile data:', profileData)
        
        let createdProfile
        try {
          // First check if profile already exists (created by trigger)
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authData.user.id)
            .single()

          if (existingProfile) {
            console.log('Profile already exists (created by trigger):', existingProfile)
            createdProfile = existingProfile
            
            // Update the profile with any additional data if needed
            if (role === 'coach' && specializations) {
              const { data: updatedProfile } = await supabase
                .from('profiles')
                .update({ 
                  specializations: specializations.split(',').map(s => s.trim()),
                  full_name: fullName // Ensure full name is set correctly
                })
                .eq('id', authData.user.id)
                .select()
                .single()
              
              if (updatedProfile) {
                createdProfile = updatedProfile
                console.log('Updated profile with specializations:', updatedProfile)
              }
            }
          } else {
            // Profile doesn't exist, create it manually
            createdProfile = await createUserProfile(authData.user, role, profileData)
            console.log('Created profile manually:', createdProfile)
          }
        } catch (profileError: any) {
          // If it's a duplicate key error, the profile was created by trigger
          if (profileError.code === '23505') {
            console.log('Profile was created by database trigger, fetching it...')
            const { data: triggerProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', authData.user.id)
              .single()
            
            if (triggerProfile) {
              createdProfile = triggerProfile
              console.log('Retrieved trigger-created profile:', triggerProfile)
            } else {
              console.error('Could not fetch trigger-created profile')
              throw new Error('Profile creation failed - could not retrieve created profile')
            }
          } else {
            console.error('PROFILE CREATION ERROR:', profileError)
            console.error('Error details:', {
              message: profileError.message,
              code: profileError.code,
              details: profileError.details,
              hint: profileError.hint
            })
            throw new Error(`Failed to create user profile: ${profileError.message}`)
          }
        }

        // If invitation code was used, create coach-coachee relationship and update code
        if (coachId && invitationCode && invitationCodeData) {
          try {
            // Try the new safe function first (bypasses RLS issues)
            const { data: relationshipResult, error: relationshipError } = await supabase
              .rpc('create_coach_coachee_relationship', {
                p_coach_id: coachId,
                p_coachee_id: authData.user.id,
                p_invitation_code: invitationCode
              })

            if (relationshipError) {
              console.warn('Safe function failed, trying direct insert:', relationshipError)
              
              // Fallback to direct insert method
              await supabase
                .from('coach_coachee_relationships')
                .insert([{
                  coach_id: coachId,
                  coachee_id: authData.user.id,
                  status: 'active'
                }])

              // Manually update invitation code usage (fallback method)
              const newUsageCount = invitationCodeData.current_uses + 1
              const isFullyUsed = newUsageCount >= invitationCodeData.max_uses
              const updateData: any = {
                current_uses: newUsageCount,
                status: 'accepted',
                // Mark as used since we're implementing single-use codes
                used: isFullyUsed,
                used_by: authData.user.id,
                used_at: new Date().toISOString()
              }

              const { error: invitationUpdateError } = await supabase
                .from('invitation_codes')
                .update(updateData)
                .eq('code', invitationCode)

              if (invitationUpdateError) {
                console.error('Failed to update invitation code status:', invitationUpdateError)
                // Don't throw here - relationship was created successfully
              }
            } else {
              console.log('Relationship created successfully:', relationshipResult)
              
              // Double-check that invitation code was updated by the safe function
              const { data: updatedCodeData, error: checkError } = await supabase
                .from('invitation_codes')
                .select('current_uses, status')
                .eq('code', invitationCode)
                .single()

              if (!checkError && updatedCodeData) {
                                 // If the invitation code wasn't updated by the safe function, update it manually
                 if (updatedCodeData.current_uses === invitationCodeData.current_uses && updatedCodeData.status === 'pending') {
                   console.log('Safe function did not update invitation code, updating manually...')
                   const newUsageCount = invitationCodeData.current_uses + 1
                   const isFullyUsed = newUsageCount >= invitationCodeData.max_uses
                   const updateData: any = {
                     current_uses: newUsageCount,
                     status: 'accepted',
                     // Mark as used since we're implementing single-use codes
                     used: isFullyUsed,
                     used_by: authData.user.id,
                     used_at: new Date().toISOString()
                   }

                  const { error: manualUpdateError } = await supabase
                    .from('invitation_codes')
                    .update(updateData)
                    .eq('code', invitationCode)

                  if (manualUpdateError) {
                    console.error('Manual invitation code update failed:', manualUpdateError)
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error creating coach-coachee relationship:', error)
            // Don't throw here - let registration complete even if relationship creation fails
            // The user can link with the coach later from their profile
          }
        }

        // Set auth state for immediate routing
        setSupabaseUser(authData.user)
        
        // Also set the user profile immediately to ensure role is available
        if (createdProfile) {
          console.log('Setting user profile in store:', createdProfile)
          const userProfile: User = {
            id: createdProfile.id,
            email: createdProfile.email,
            full_name: createdProfile.full_name,
            role: createdProfile.role,
            avatar_url: createdProfile.avatar_url || '',
            onboarding_completed: createdProfile.onboarding_completed || false,
            show_motivational_messages: createdProfile.show_motivational_messages !== false,
            created_at: authData.user.created_at,
            updated_at: createdProfile.updated_at || authData.user.created_at,
          }
          setUser(userProfile)
          console.log('User profile set in store')
        } else {
          console.warn('No createdProfile available - this might prevent proper redirection')
        }
        
        console.log('About to show success message and redirect...')
        
        // Show success message and redirect to onboarding
        showToastMessage(
          `Registration successful! Welcome to InsightFlow. You'll now complete the onboarding process.`,
          'success'
        )
        
        console.log('Success message shown, setting up redirect...')
        
        // Redirect to onboarding after a short delay
        setTimeout(() => {
          console.log('Redirecting to onboarding...')
          history.push('/onboarding')
        }, 2000)
      }
    } catch (error: any) {
      showToastMessage(error.message || t('auth.register.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-500 to-secondary-700 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            {/* Header with Back Button and Language Switcher */}
            <div className="mb-4 flex justify-between items-center">
              <IonButton 
                fill="clear" 
                size="small"
                onClick={() => history.push('/')}
                className="text-gray-600"
              >
                <IonIcon icon={arrowBackOutline} slot="start" />
                {t('common.backToHome')}
              </IonButton>
              <LanguageSwitcher size="small" mobileOnly={true} />
            </div>
            
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
                {t('auth.register.title')}
              </h1>
              <p className="text-gray-600">
                {t('auth.register.subtitle')}
              </p>
            </div>

            <form onSubmit={handleRegister} className="space-y-6">
              {/* Role Selection - Admin option removed */}
              <div className="mb-6">
                <IonLabel className="block text-sm font-medium text-gray-700 mb-3">
                  {t('auth.register.roleLabel')}
                </IonLabel>
                <IonRadioGroup value={role} onIonChange={e => setRole(e.detail.value)}>
                  <IonList>
                    <IonItem className="rounded-lg mb-2 border border-gray-200">
                      <IonIcon icon={businessOutline} slot="start" color="primary" />
                      <IonLabel>
                        <h3 className="font-medium">{t('auth.register.coach')}</h3>
                        <p className="text-sm text-gray-600">{t('auth.register.coachDescription')}</p>
                      </IonLabel>
                      <IonRadio slot="end" value="coach" />
                    </IonItem>
                    
                    <IonItem className="rounded-lg border border-gray-200">
                      <IonIcon icon={personOutline} slot="start" color="secondary" />
                      <IonLabel>
                        <h3 className="font-medium">{t('auth.register.coachee')}</h3>
                        <p className="text-sm text-gray-600">{t('auth.register.coacheeDescription')}</p>
                      </IonLabel>
                      <IonRadio slot="end" value="coachee" />
                    </IonItem>
                  </IonList>
                </IonRadioGroup>
                
                {/* Admin Role Information */}
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <IonText className="text-xs text-blue-700">
                    <strong>Need admin access?</strong><br/>
                    Register as a regular user first. An existing administrator can then promote your account to admin status.
                  </IonText>
                </div>
              </div>

              <IonItem className="rounded-lg">
                <IonLabel position="stacked">{t('auth.register.fullNameLabel')}</IonLabel>
                <IonInput
                  type="text"
                  value={fullName}
                  onIonInput={(e) => setFullName(e.detail.value!)}
                  required
                  placeholder={t('auth.register.fullNamePlaceholder')}
                />
              </IonItem>

              <IonItem className="rounded-lg">
                <IonLabel position="stacked">{t('auth.register.emailLabel')}</IonLabel>
                <IonInput
                  type="email"
                  value={email}
                  onIonInput={(e) => setEmail(e.detail.value!)}
                  required
                  placeholder={t('auth.register.emailPlaceholder')}
                />
              </IonItem>

              <IonItem className="rounded-lg">
                <IonLabel position="stacked">{t('auth.register.passwordLabel')}</IonLabel>
                <IonInput
                  type="password"
                  value={password}
                  onIonInput={(e) => setPassword(e.detail.value!)}
                  required
                  placeholder={t('auth.register.passwordPlaceholder')}
                  minlength={6}
                />
              </IonItem>

              {/* Coach-specific fields */}
              {role === 'coach' && (
                <IonItem className="rounded-lg">
                  <IonLabel position="stacked">
                    {t('auth.register.specializationsLabel')}
                  </IonLabel>
                  <IonInput
                    type="text"
                    value={specializations}
                    onIonInput={(e) => setSpecializations(e.detail.value!)}
                    placeholder={t('auth.register.specializationsPlaceholder')}
                  />
                </IonItem>
              )}

              {/* Invitation Code for Coachees */}
              {role === 'coachee' && (
                <IonItem className="rounded-lg">
                  <IonLabel position="stacked">
                    Invitation Code (optional)
                  </IonLabel>
                  <IonInput
                    type="text"
                    value={invitationCode}
                    onIonInput={(e) => setInvitationCode(e.detail.value!)}
                    placeholder="Enter invitation code from your coach"
                  />
                </IonItem>
              )}

              <IonButton
                expand="block"
                type="submit"
                disabled={loading}
                className="mt-6"
                color={role === 'coach' ? 'primary' : 'secondary'}
              >
                {loading ? t('auth.register.creatingAccount') : t('auth.register.createAccountButton', { role: t(`auth.register.${role}`) })}
              </IonButton>
            </form>

            <div className="mt-6 text-center">
              <IonText color="medium" className="text-sm">
                {t('auth.register.alreadyHaveAccount')}{' '}
                <span 
                  onClick={() => history.push('/login')}
                  className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium underline"
                >
                  {t('auth.register.signIn')}
                </span>
              </IonText>
              
              {/* Help text for existing users */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <IonText className="text-xs text-blue-700">
                  <strong>Having trouble?</strong><br/>
                  If you get an "already exists" error, the email is already registered. 
                  Try <span 
                    onClick={() => history.push('/login')}
                    className="underline cursor-pointer font-medium"
                  >signing in</span> or <span 
                    onClick={() => history.push('/reset-password')}
                    className="underline cursor-pointer font-medium"
                  >resetting your password</span>.
                </IonText>
              </div>

              {/* Future email confirmation note */}
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                <IonText className="text-xs text-yellow-700">
                  <strong>Note:</strong> Email confirmation will be required in a future update for enhanced security.
                </IonText>
              </div>
            </div>
          </div>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={toastColor === 'success' ? 4000 : 3000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  )
}

export default RegisterPage 